import React, { ReactElement, useMemo } from 'react';
import classNames from 'classnames';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Divider, Icon, AlyceTheme } from '@alycecom/ui';

import StepSection from '../StepSection/StepSection';
import { getSingleDeclineTitle } from '../../helpers/unsubscribe.helpers';
import { IGift } from '../../types/gift';

const useStyles = makeStyles<AlyceTheme>(({ spacing }) => ({
  textMargin: {
    marginBottom: spacing(3),
  },
  textArea: {
    width: 550,
    '@media (max-width:700px)': {
      width: '100%',
    },
  },
  feedbackSuccessIcon: {
    fontSize: 25,
    marginRight: spacing(1),
    marginBottom: 6,
  },
  submitButton: {
    width: 275,
    '@media (max-width:420px)': {
      width: '100%',
    },
  },
}));

interface IDeclineSuccessStep {
  gift: IGift;
}

const DeclineSuccessStep = ({ gift }: IDeclineSuccessStep): ReactElement => {
  const classes = useStyles();
  const { recipient, isUnsubscribed, sender, company } = gift;

  const singleDeclineTitle = useMemo(() => getSingleDeclineTitle(company, sender), [sender, company]);

  return (
    <StepSection
      data-testid="declineSuccessStepSection"
      title="Step 1. Decline or unsubscribe gift invites via Alyce"
      iconProps={{ icon: 'check', color: 'default-salad' }}
    >
      <Typography
        data-testid="declineSuccessStep-userEmailMessageText"
        className={classNames('Body-Regular-Left-Static', classes.textMargin)}
      >
        Your email address <b>{recipient.email}</b>{' '}
        {isUnsubscribed ? 'will no longer receive' : 'has successfully declined'}:
      </Typography>

      <Typography
        data-testid="declineSuccessStep-userInvitationMessageText"
        className={classNames('H4-Dark-Bold', classes.textMargin)}
        component="h4"
      >
        {isUnsubscribed ? 'Any future gift invitations processed through Alyce' : singleDeclineTitle}
      </Typography>

      <Typography data-testid="declineSuccessStep-userActionMessage" className="Body-Regular-Left-Inactive">
        If this is all you want, simply cross out the window and you’re all set!
      </Typography>

      {isUnsubscribed && (
        <>
          <Divider mt={4} mb={4} />
          <Typography
            data-testid="declineSuccessStep-userGoodbyeMessageText"
            className={classNames('H3-Chambray', classes.textMargin)}
            component="h3"
          >
            Sometimes it’s okay to say goodbye, our relationship will remain in hearts.
          </Typography>
        </>
      )}

      <Box mt={2} display="flex" flexDirection="row" justifyContent="flex-start" alignItems="center">
        <Icon className={classes.feedbackSuccessIcon} icon="check" color="default-salad" />
        <Typography data-testid="declineSuccessStep-userFeedbackMessageText" className="Body-Regular-Left-Success">
          You feedback has been submitted. Thank you! We really appreciate it.
        </Typography>
      </Box>
    </StepSection>
  );
};

export default DeclineSuccessStep;
